import { NgFor } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { AnimalGroupBarnMapping } from 'src/app/cycle/models/cycle.model';
import Utils from 'src/app/shared/utils';

import { CodelistVaccine, EditAnimalGroupMode, VaccinationDataRow } from '../animal-group.model';

export interface VaccinationEditDialogInputData extends AbstractDialogData {
  vaccinationRow?: VaccinationDataRow;
  mode: EditAnimalGroupMode;
  barnMappings: AnimalGroupBarnMapping[];
  codelistVaccines: CodelistVaccine[];
  countries: string[];
  locationCountry: string;
}

export interface VaccinationEditDialogResultData {
  vaccinationRow: VaccinationDataRow;
}

@Component({
  selector: 'app-vaccination-edit.component',
  templateUrl: 'vaccination-edit.component.html',
  styleUrl: 'vaccination-edit.component.scss',
  imports: [
    DialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    TranslatePipe,
  ],
})
export class VaccinationEditComponent
  extends AbstractDialog<VaccinationEditDialogResultData>
  implements OnInit
{
  data = inject<VaccinationEditDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<VaccinationEditComponent, AbstractDialogResultData>>(MatDialogRef);
  private readonly localeService = inject(LocaleService);

  id: number;
  codelistVaccinesByCountry: CodelistVaccine[] = [];
  selectedCountry: string = null;

  formGroup = new FormGroup({
    barn: new FormControl<AnimalGroupBarnMapping>(null, [Validators.required]),
    date: new FormControl<Date>(null, [Validators.required]),
    country: new FormControl<string>(null, [Validators.required]),
    vaccine: new FormControl<CodelistVaccine>(null, [Validators.required]),
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.data.title = this.localeService.translate(this.data.mode);
  }

  ngOnInit() {
    if (this.data.mode === EditAnimalGroupMode.Edit && this.data.vaccinationRow !== null) {
      const selectedBarn = this.data.barnMappings.find(
        (b) => b.barnId === this.data.vaccinationRow?.barn?.barnId,
      );
      const selectedVaccine = this.data.codelistVaccines.find(
        (v) => v.plukonId === this.data.vaccinationRow.vaccine.plukonId,
      );
      this.selectedCountry = this.data.vaccinationRow.country;

      this.formGroup.controls.barn.setValue(selectedBarn);
      this.formGroup.controls.date.setValue(new Date(this.data.vaccinationRow.date));
      this.formGroup.controls.country.setValue(this.selectedCountry);
      this.formGroup.controls.vaccine.setValue(selectedVaccine);
    } else {
      this.selectedCountry = this.data.locationCountry;
    }
  }

  updateCodelistVccinesByCountry(country: string) {
    this.codelistVaccinesByCountry = this.data.codelistVaccines.filter(
      (v) => v.country === country,
    );
  }

  countryChanged(country: string) {
    this.updateCodelistVccinesByCountry(country);
  }

  save() {
    this.saveVaccination();
  }

  private saveVaccination() {
    if (this.formGroup.valid) {
      super.confirm({
        vaccinationRow: {
          id: this.data.vaccinationRow?.id,
          barn: this.formGroup.controls.barn.value,
          date: Utils.parseDateTimeFromFormValue(this.formGroup.controls.date.value),
          country: this.formGroup.controls.country.value,
          vaccine: this.formGroup.controls.vaccine.value,
        },
      });
    }
  }
}
